import { Link } from 'react-router-dom';
import { TERMS_VERSION } from '../lib/marketplace';
import { cn, ui } from '../lib/ui';

/**
 * /marketplace/terms: the marketplace terms a vendor accepts when applying
 * (docs/Decisions.md D68 items 1 to 7). The source text is
 * docs/legal/MarketplaceTerms.md, a draft for counsel; the version a vendor
 * accepted is stored on its listing.
 */
export default function MarketplaceTerms() {
  return (
    <article className="max-w-prose">
      <p className="mb-4">
        <Link to="/vendor">Back to the vendor workspace</Link>
      </p>
      <h1 className={ui.h1}>Marketplace terms</h1>
      <p className={cn(ui.hint, 'mt-0')}>Version {TERMS_VERSION}. Draft pending legal review.</p>

      <h2 className={ui.h2}>1. Who decides</h2>
      <p>
        Each organization runs its own marketplace. Its org admins, and members of groups they name,
        approve or decline vendors, and its org admins can suspend or remove a listing and moderate
        reviews at any time. The org admins of an organization have the final say on listings and
        reviews in it. The platform sets the review policies, categories and fee limits every
        organization works within, and can suspend a vendor everywhere.
      </p>

      <h2 className={ui.h2}>2. Applying and vetting</h2>
      <p>
        You join an organization before you apply to its marketplace, and you choose its categories.
        Some categories are regulated or licensed: you give the license, permit or document details
        they list, and the organization validates every one before approving you. Keep them current;
        an expired or false credential is a reason to remove a listing.
      </p>

      <h2 className={ui.h2}>3. Fees</h2>
      <ul>
        <li>
          You pay a <strong>lead fee</strong> on a deal sourced through the organization: a lead
          that joined your interest list there and that you mark won. Each category shows its lead
          fee before you apply: a fixed amount, or a percentage of the deal value you report.
        </li>
        <li>
          The lead fee is split. The <strong>organization fee</strong>, set by the org admin, goes
          to the organization, and the platform fee is taken from it. A{' '}
          <strong>recommender reward</strong> in money, if your program offers one, comes next. The
          balance goes to the <strong>salesperson or broker</strong> you assign to the lead. If you
          sell the deal yourself, you are not charged the balance.
        </li>
        <li>A change to a category&apos;s fees applies to leads created after it.</li>
        <li>In investment organizations every fee is a flat amount.</li>
      </ul>

      <h2 className={ui.h2}>4. Reporting honestly</h2>
      <p>
        Members are asked whether they bought and at what price. Your sales tracker should match
        what members report; repeated mismatches lead to a review of your listing.
      </p>

      <h2 className={ui.h2}>5. Members&apos; contact details</h2>
      <p>
        You see a member&apos;s contact details only when they agree to share them with you, and
        only while they stay on your interest list. Use them only to discuss the service they asked
        about.
      </p>

      <h2 className={ui.h2}>6. Reviews and recommendations</h2>
      <p>
        Only members who bought from you can review you, and you may respond publicly. You may not
        offer anything for a review. Anyone may recommend you; your recommender program decides only
        what a recommendation earns, and recommendations that can earn are labeled as such.
      </p>

      <h2 className={ui.h2}>7. What the platform does not do</h2>
      <p>
        The platform does not take payment for your services, hold members&apos; money, or guarantee
        any vendor. Members deal with you directly.
      </p>
    </article>
  );
}
